import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MemoryIcon from '@mui/icons-material/Memory';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Link as RouterLink,
  NavLink,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router';
import {
  canManage,
  isAdminHome,
  isSupporting,
  seesProjects,
  seesWarehouses,
  useSession,
} from '../auth/session';
import { placeKind } from '@fleetspoke/core/places';
import { useOpenAlertCount } from '../data/alerts';
import { requestKeys, useRequestActionCount } from '../data/requests';
import { useSites } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import { useAllTransfers, useCount } from '../data/workflow';
import { transferHome } from '../lib/places';
import { BrandLockup } from './Brand';
import { LanguageMenu } from './LanguageMenu';
import { SUPPORT_BAR_HEIGHT, SupportBanner } from './SupportBanner';

const DRAWER = 232;
const RAIL = 64;
const COLLAPSED_KEY = 'fsa.nav.collapsed';
const SETTINGS_KEY = 'fsa.nav.settings';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** a count chip (the Requests queue, Q16): red when any of them is urgent or late */
  chip?: { count: number; hot: boolean };
}

const chipText = (n: number) => (n > 99 ? '99+' : String(n));

// Remembered per browser (P1). Storage can be missing or refuse (private mode): the menu then just starts open.
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // not remembered; the choice still holds for this visit
  }
}

// The Settings group (CR_20260924_Dashboard_Revamp §2): starts closed, remembered per browser like the rail.
function readSettingsOpen(): boolean {
  try {
    return localStorage.getItem(SETTINGS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSettingsOpen(value: boolean) {
  try {
    localStorage.setItem(SETTINGS_KEY, value ? '1' : '0');
  } catch {
    // not remembered; the choice still holds for this visit
  }
}

/**
 * Which menu entry owns the current page (P16): places by their kind, a count by its site's kind, a transfer by the
 * place it belongs to for the viewer (destination first, else origin; CR_20260915_Request_Qty R0). Alerts has no
 * menu entry: nothing is highlighted there.
 */
function useMenuOwner(pathname: string): string | null {
  const countMatch = useMatch('/counts/:countId');
  const transferMatch = useMatch('/transfers/:transferId');
  const count = useCount(countMatch?.params.countId ?? '', !!countMatch);
  const transfers = useAllTransfers(!!transferMatch);
  const sites = useSites(!!countMatch || !!transferMatch);
  const kindOf = (site: { type: Parameters<typeof placeKind>[0] } | null | undefined) =>
    site ? (placeKind(site.type) === 'warehouse' ? '/warehouses' : '/projects') : null;
  if (pathname.startsWith('/warehouses')) return '/warehouses';
  if (pathname.startsWith('/projects') || pathname.startsWith('/locations')) return '/projects';
  if (countMatch) return kindOf(sites.data?.find((s) => s.site_id === count.data?.count.site_id));
  if (transferMatch) {
    const tr = transfers.data?.find((x) => x.id === transferMatch.params.transferId);
    return tr ? kindOf(transferHome(tr, sites.data ?? [])) : null;
  }
  if (pathname.startsWith('/alerts')) return '';
  return null;
}

export function AppShell() {
  const { t } = useTranslation();
  const { me, signOut, supportEnded, clearSupportEnded } = useSession();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const location = useLocation();
  const owner = useMenuOwner(location.pathname);
  // on a settings page the group is open so the page is visible; the stored preference is left alone
  const onSettings = location.pathname.startsWith('/settings');
  const [settingsOpen, setSettingsOpen] = useState(() => onSettings || readSettingsOpen());
  const [wasOnSettings, setWasOnSettings] = useState(onSettings);
  if (onSettings !== wasOnSettings) {
    setWasOnSettings(onSettings);
    if (onSettings) setSettingsOpen(true);
  }
  const [settingsMenu, setSettingsMenu] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const adminHome = isAdminHome(me);
  const supporting = isSupporting(me);
  const openAlerts = useOpenAlertCount(!!me && !adminHome);
  useLiveInvalidate('alert-bell', ['alerts'], [['alerts', 'open-count']], 1500);
  // the Requests chip: managers approve, warehouse staff prepare; project users have none (their inbox tells them)
  const queued = !!me?.company && !adminHome && (canManage(me) || me.role === 'warehouse_staff');
  const requestCount = useRequestActionCount(queued);
  useLiveInvalidate('request-chip', ['asset_requests', 'transfers'], [requestKeys.count], 1500);

  // a super admin outside any customer account only has the admin area (CR_20260913 part C)
  if (adminHome && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/companies" replace />;
  }

  const manager = canManage(me);
  const main: NavItem[] = adminHome
    ? []
    : [
        { to: '/', label: t('nav.dashboard'), icon: <DashboardIcon /> },
        // a project user never sees a warehouse; warehouse staff see Projects once given projects (P1)
        ...(seesWarehouses(me)
          ? [{ to: '/warehouses', label: t('nav.warehouses'), icon: <WarehouseIcon /> }]
          : []),
        // billing inside is managers-only (P7)
        ...(seesProjects(me)
          ? [{ to: '/projects', label: t('nav.projects'), icon: <EngineeringIcon /> }]
          : []),
        { to: '/assets', label: t('nav.assets'), icon: <Inventory2Icon /> },
        {
          to: '/requests',
          label: t('nav.requests'),
          icon: <AssignmentIcon />,
          chip:
            queued && requestCount.data?.waiting
              ? { count: requestCount.data.waiting, hot: requestCount.data.hot > 0 }
              : undefined,
        },
        // Transfers live on their places' Transfers tab, Alerts behind the bell (CR_20260915_Request_Qty Q15)
        ...(manager ? [{ to: '/reports', label: t('nav.reports'), icon: <AssessmentIcon /> }] : []),
      ];
  const settings: NavItem[] = canManage(me)
    ? [
        { to: '/settings/users', label: t('nav.settings_users'), icon: <GroupIcon /> },
        { to: '/settings/categories', label: t('nav.settings_categories'), icon: <CategoryIcon /> },
        { to: '/settings/rates', label: t('nav.settings_rates'), icon: <RequestQuoteIcon /> },
        {
          to: '/settings/approvals',
          label: t('nav.settings_approvals'),
          icon: <HowToRegOutlinedIcon />,
        },
        {
          to: '/settings/notifications',
          label: t('nav.settings_notifications'),
          icon: <NotificationsActiveIcon />,
        },
        { to: '/settings/support', label: t('nav.settings_support'), icon: <SupportAgentIcon /> },
      ]
    : [];

  // while supporting a customer the admin area is out of reach (S7): Exit in the banner goes back
  const admin: NavItem[] =
    me?.platform_admin && !supporting
      ? [
          { to: '/admin/companies', label: t('nav.admin_companies'), icon: <BusinessIcon /> },
          { to: '/admin/devices', label: t('nav.admin_devices'), icon: <MemoryIcon /> },
          {
            to: '/admin/super-admins',
            label: t('nav.admin_super_admins'),
            icon: <AdminPanelSettingsIcon />,
          },
        ]
      : [];

  const isActive = (to: string) =>
    owner !== null
      ? owner === to
      : to === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(to);
  // icons only on desktop when collapsed; the slide-in menu on phones is always full
  const rail = desktop && collapsed;
  const width = rail ? RAIL : DRAWER;
  const toggleRail = () =>
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  const toggleSettings = () => {
    writeSettingsOpen(!settingsOpen);
    setSettingsOpen(!settingsOpen);
  };
  const items = (list: NavItem[], indent = false) =>
    list.map((item) => {
      const button = (
        <ListItemButton
          key={item.to}
          component={NavLink}
          to={item.to}
          selected={isActive(item.to)}
          onClick={() => setOpen(false)}
          aria-label={rail ? item.label : undefined}
          sx={rail ? { justifyContent: 'center', px: 0 } : indent ? { pl: 4 } : undefined}
        >
          <ListItemIcon sx={rail ? { minWidth: 0 } : undefined}>
            {rail && item.chip ? (
              <Badge
                badgeContent={chipText(item.chip.count)}
                color={item.chip.hot ? 'error' : 'primary'}
                data-testid={`nav-badge-${item.to.slice(1)}`}
              >
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          {rail ? null : <ListItemText primary={item.label} />}
          {!rail && item.chip ? (
            <Chip
              size="small"
              label={chipText(item.chip.count)}
              color={item.chip.hot ? 'error' : 'default'}
              data-testid={`nav-chip-${item.to.slice(1)}`}
            />
          ) : null}
        </ListItemButton>
      );
      return rail ? (
        <Tooltip key={item.to} title={item.label} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      );
    });
  const nav = (
    <Box
      sx={{ width, overflowX: 'hidden', transition: theme.transitions.create('width') }}
      role="navigation"
      data-testid={rail ? 'nav-rail' : 'nav-full'}
    >
      <Toolbar />
      {supporting ? <Box sx={{ height: SUPPORT_BAR_HEIGHT }} /> : null}
      {main.length > 0 && <List dense>{items(main)}</List>}
      {admin.length > 0 && (
        <>
          {main.length > 0 && <Divider />}
          <List dense>{items(admin)}</List>
        </>
      )}
      {settings.length > 0 && (
        <>
          <Divider />
          {rail ? (
            // icon rail: one gear, its pages in a menu beside it
            <List dense>
              <Tooltip title={t('nav.settings')} placement="right">
                <ListItemButton
                  selected={onSettings}
                  onClick={(e) => setSettingsMenu(e.currentTarget)}
                  aria-label={t('nav.settings')}
                  aria-haspopup="menu"
                  aria-expanded={!!settingsMenu}
                  sx={{ justifyContent: 'center', px: 0 }}
                  data-testid="nav-settings"
                >
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <SettingsIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
              <Menu
                anchorEl={settingsMenu}
                open={!!settingsMenu}
                onClose={() => setSettingsMenu(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {settings.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    selected={isActive(item.to)}
                    onClick={() => setSettingsMenu(null)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </MenuItem>
                ))}
              </Menu>
            </List>
          ) : (
            <List dense>
              <ListItemButton
                onClick={toggleSettings}
                aria-expanded={settingsOpen}
                aria-controls="nav-settings-list"
                data-testid="nav-settings"
              >
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary={t('nav.settings')} />
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transition: theme.transitions.create('transform'),
                    transform: settingsOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </ListItemButton>
              <Collapse in={settingsOpen} unmountOnExit id="nav-settings-list">
                {items(settings, true)}
              </Collapse>
            </List>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ zIndex: (th) => th.zIndex.drawer + 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar>
          {desktop ? (
            <Tooltip title={collapsed ? t('nav.expand') : t('nav.collapse')}>
              <IconButton
                edge="start"
                onClick={toggleRail}
                aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
                aria-expanded={!collapsed}
                sx={{ mr: 1 }}
                data-testid="nav-toggle"
              >
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton edge="start" onClick={() => setOpen(true)} aria-label="menu" sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Link
            component={RouterLink}
            to={adminHome ? '/admin/companies' : '/'}
            underline="none"
            sx={{ display: 'flex', mr: 2, borderRadius: 1 }}
          >
            <BrandLockup size="sm" markOnlyOnPhone />
          </Link>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mr: 2, my: 2, display: { xs: 'none', sm: 'block' } }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flexGrow: 1, fontWeight: 500 }}
          >
            {adminHome ? t('nav.admin') : (me?.company?.name ?? '')}
          </Typography>
          {!adminHome && (
            <Tooltip title={t('nav.alerts')}>
              <IconButton
                onClick={() => void navigate('/alerts')}
                aria-label={t('nav.alerts')}
                data-testid="alert-bell"
              >
                <Badge badgeContent={openAlerts.data ?? 0} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          <LanguageMenu />
          <Tooltip title={t('common.sign_out')}>
            <IconButton onClick={() => void signOut()} aria-label={t('common.sign_out')}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
        {me?.support ? (
          <SupportBanner
            support={me.support}
            timeZone={me.company?.timezone ?? 'Asia/Kuala_Lumpur'}
          />
        ) : null}
      </AppBar>
      {desktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width,
            flexShrink: 0,
            transition: theme.transitions.create('width'),
            '& .MuiDrawer-paper': {
              width,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: theme.transitions.create('width'),
            },
          }}
        >
          {nav}
        </Drawer>
      ) : (
        <Drawer open={open} onClose={() => setOpen(false)}>
          {nav}
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Toolbar />
        {supporting ? <Box sx={{ height: SUPPORT_BAR_HEIGHT }} /> : null}
        <Outlet />
      </Box>
      <Snackbar
        open={!!supportEnded && !supporting}
        onClose={clearSupportEnded}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={clearSupportEnded}>
          {t('support.ended', { company: supportEnded ?? '' })}
        </Alert>
      </Snackbar>
    </Box>
  );
}
